import intl from 'react-intl-universal';
import { Fragment, useMemo, useState, useCallback, useEffect } from 'react';

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// functions
import useApi from 'shared/hooks/api';
import localData from 'utils/localData';
import { updateArrayItemById } from 'utils/javascript';
import { getUserFullName } from 'utils/name';
import { getFirstDayPreviousMonth } from 'utils/dateTime';

// constants
import { DOLPHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';

// components and parts
import { PageLoader, PageError } from 'components';
import { getReceivedTableColumns } from './tableColumns';
import History from 'views/History';
import logger from 'utils/logger';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
const log = logger.createLogger('ReceivedHistory');

const REFRESH_TIME = 1000;

const propTypes = {
  type: PropTypes.string,
};

const DashboardReceivedHistory = ({ type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const firstDayPreviousMonth = getFirstDayPreviousMonth(currentDate);
  const [timeRangeStart, setTimeRangeStart] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.HISTORY_START_DATE);
    return saved ? new Date(saved) : firstDayPreviousMonth;
  });
  const [timeRangeEnd, setTimeRangeEnd] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.HISTORY_END_DATE);
    return saved ? new Date(saved) : currentDate;
  });
  const [filteredTableData, setFilteredTableData] = useState([]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.HISTORY_START_DATE,
      timeRangeStart.toISOString()
    );
  }, [timeRangeStart]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.HISTORY_END_DATE,
      timeRangeEnd.toISOString()
    );
  }, [timeRangeEnd]);

  const handleOpenIssueDetails = useCallback(
    (issueId) => {
      navigate(`${location.pathname}/issues/${issueId}`);
    },
    [navigate, location.pathname]
  );
  const columns = useMemo(
    () => getReceivedTableColumns(handleOpenIssueDetails),
    [handleOpenIssueDetails]
  );

  const [{ data, error, setLocalData }, fetchReceivedIssues] = useApi.useGet(
    DOLPHIN_API.ISSUES_RECEIVED,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        type: type,
        timeRangeStart: timeRangeStart,
        timeRangeEnd: timeRangeEnd,
      },
    }
  );

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { receivedIssues, doctors } = data;

  // TODO Reload the page since the data might be updated
  const updateLocalProjectIssues = (issueId, updatedFields) => {
    setLocalData((currentData) => ({
      receivedIssues: updateArrayItemById(
        currentData.receivedIssues,
        issueId,
        updatedFields
      ),
      doctors: doctors,
    }));
    // the function passed into the brackets of setLocalData(...) is the `getUpdatedData` in setLocalData in /shared/hooks/api/query.js;
    // Therefore currentData is `data` in the line `const updatedData = getUpdatedData(data);`;
  };
  receivedIssues.sort((issue1, issue2) => issue2.id - issue1.id);

  const tableData = receivedIssues.map((issue) => {
    const doctorsInCharge = doctors.filter((user) =>
      issue.userIds.includes(user.id)
    );
    const doctorList = doctorsInCharge.map((doctor) =>
      getUserFullName(doctor.firstName, doctor.lastName)
    );

    return {
      case_title: issue.title,
      status: issue.status,
      state: issue.state,
      emergency: issue.priority,
      deadline: issue.deadline,
      order_time: issue.createdAt,
      from: issue.srcOrgName,
      returned_time: issue.returnedAt,
      doctor:
        doctorList.length > 0
          ? doctorList
          : [intl.get('case_list_value_doctor_unassigned')],
      view: issue.id,
    };
  });

  const handleRowModelChange = (rowModel) => {
    // Convert the row model to the actual data
    const filteredData = rowModel.map((row) => row.original);
    const formattedData = filteredData.map((issue) => {
      return {
        id: issue.view,
        title: issue.case_title,
        patientName: issue.patientName,
        state: issue.state,
        status: issue.status,
        to_hospital: issue.to_hospital,
        order_time: issue.order_time,
        returned_time: issue.returned_time,
      };
    });
    setFilteredTableData(formattedData);
  };

  return (
    <Fragment>
      <History
        columns={columns}
        tableData={tableData}
        tableTitle={intl.get('case_list_text_received_table_title')}
        handleOpenIssueDetails={handleOpenIssueDetails}
        updateLocalProjectIssues={(issueId, updatedFields) => {
          updateLocalProjectIssues(issueId, updatedFields);
          setTimeout(() => {
            fetchReceivedIssues();
          }, REFRESH_TIME);
        }}
        reloadData={fetchReceivedIssues}
        setTimeRangeEnd={setTimeRangeEnd}
        setTimeRangeStart={setTimeRangeStart}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        issues={receivedIssues}
        doctors={doctors}
        isSender={false}
        onRowModelChange={handleRowModelChange}
      />
      <Outlet
        context={{
          doctors,
          updateLocalProjectIssues,
          issues: filteredTableData,
        }}
      />
    </Fragment>
  );
};

DashboardReceivedHistory.propTypes = propTypes;

export default DashboardReceivedHistory;
